import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Heart, ShieldCheck, Sparkles, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { CartDrawer } from "../components/CartDrawer";
import { resolveImage, formatCurrency } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReservedItem() {
  const { token } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addItem, setIsOpen, count } = useCart();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products/private/${token}`);
        if (!cancelled) setProduct(data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? "This reserved item is no longer available."
              : "We couldn't load this item. Please refresh and try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAddAndCheckout = () => {
    if (!product) return;
    addItem(product);
    setIsOpen(true);
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center font-script text-2xl"
        style={{ color: "var(--text-accent)", backgroundColor: "var(--bg-primary)" }}
        data-testid="reserved-loading"
      >
        opening your reserved item…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: "var(--bg-primary)" }}
      >
        <div
          className="max-w-md w-full bg-white rounded-3xl p-10 text-center"
          style={{ border: "1px solid var(--border-soft)" }}
          data-testid="reserved-error"
        >
          <p className="font-script text-2xl mb-3" style={{ color: "var(--text-accent)" }}>
            oh no
          </p>
          <h1 className="font-display text-3xl mb-4" style={{ color: "var(--text-primary)" }}>
            This link is no longer active
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            {error} If you think this is a mistake, please reach back out to The Giving Stitch.
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-semibold"
            style={{ backgroundColor: "var(--brand-sage)", color: "#fff" }}
          >
            Visit the main shop
          </Link>
        </div>
      </div>
    );
  }

  const sc = product.stock_count;
  const outOfStock = product.in_stock === false || (sc != null && sc <= 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg-primary)" }} data-testid="reserved-item-page">
      {/* Top bar */}
      <header
        className="px-6 py-5 flex items-center justify-between"
        style={{ borderBottom: "1px solid var(--border-soft)" }}
      >
        <Link to="/" className="font-display text-xl tracking-tight" style={{ color: "var(--text-primary)" }}>
          The Giving Stitch
        </Link>
        <button
          onClick={() => setIsOpen(true)}
          className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
          style={{ backgroundColor: "var(--brand-sage)", color: "#fff" }}
          data-testid="reserved-open-cart"
        >
          <ShoppingBag size={15} />
          Cart
          {count > 0 && (
            <span
              className="ml-1 text-xs px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: "#fff", color: "var(--brand-sage)" }}
              data-testid="reserved-cart-badge"
            >
              {count}
            </span>
          )}
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-6 pt-16 pb-24">
        {/* Hero heading */}
        <div className="text-center mb-12">
          <p
            className="font-script text-2xl sm:text-3xl mb-2"
            style={{ color: "var(--text-accent)" }}
          >
            just for you
          </p>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4"
            style={{ color: "var(--text-primary)" }}
            data-testid="reserved-heading"
          >
            This special item is reserved for you
          </h1>
          <p
            className="max-w-xl mx-auto text-base"
            style={{ color: "var(--text-secondary)" }}
          >
            We crocheted this one with care just for you. It isn&apos;t listed in the public shop — this private link is your way to take it home.
          </p>
        </div>

        {/* Product card */}
        <div
          className="grid lg:grid-cols-2 gap-10 bg-white rounded-3xl overflow-hidden"
          style={{ border: "1px solid var(--border-soft)", boxShadow: "0 8px 30px rgba(74,93,78,0.08)" }}
        >
          <div className="bg-[var(--bg-secondary)] aspect-square lg:aspect-auto">
            <img
              src={resolveImage(product.image_url)}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="reserved-product-image"
            />
          </div>
          <div className="p-8 sm:p-10 flex flex-col">
            <span
              className="text-[11px] uppercase tracking-[0.2em] font-semibold mb-3 inline-flex items-center gap-1.5 w-fit px-3 py-1 rounded-full"
              style={{ backgroundColor: "rgba(122,90,170,0.12)", color: "#6b4ea5" }}
            >
              <Sparkles size={11} />
              Reserved · private link
            </span>
            <h2
              className="font-display text-3xl sm:text-4xl mb-3"
              style={{ color: "var(--text-primary)" }}
              data-testid="reserved-product-name"
            >
              {product.name}
            </h2>
            <p
              className="text-[15px] leading-relaxed mb-6"
              style={{ color: "var(--text-secondary)" }}
              data-testid="reserved-product-description"
            >
              {product.description}
            </p>

            <div className="flex items-baseline gap-3 mb-6">
              <span
                className="font-display text-3xl"
                style={{ color: "var(--brand-sage)" }}
                data-testid="reserved-product-price"
              >
                {formatCurrency(product.price)}
              </span>
              {sc != null && sc > 0 && (
                <span className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                  {sc} available
                </span>
              )}
            </div>

            <button
              onClick={handleAddAndCheckout}
              disabled={outOfStock}
              className="w-full py-3.5 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: outOfStock ? "var(--text-secondary)" : "var(--brand-sage)", color: "#fff" }}
              data-testid="reserved-buy-button"
            >
              {outOfStock ? "Sold out" : (
                <>
                  <ShoppingBag size={16} />
                  Add to cart & checkout
                </>
              )}
            </button>

            <ul className="mt-6 space-y-2 text-xs" style={{ color: "var(--text-secondary)" }}>
              <li className="flex items-start gap-2">
                <ShieldCheck size={14} className="mt-0.5" style={{ color: "var(--brand-sage)" }} />
                Secure checkout through Stripe — choose ship or local pickup
              </li>
              <li className="flex items-start gap-2">
                <Heart size={14} className="mt-0.5" style={{ color: "var(--brand-orange)" }} />
                A portion of every sale goes to families & kids in need
              </li>
            </ul>
          </div>
        </div>

        <p
          className="text-center text-xs mt-10"
          style={{ color: "var(--text-secondary)" }}
        >
          Please don&apos;t share this link — it was created just for you.
        </p>
      </main>

      <CartDrawer />
    </div>
  );
}
